import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

const bodySx = {
  color: "text.secondary",
  lineHeight: 1.75,
} as const;

const pdfs = [
  {
    title: "Decision guide for a diabetes loop, ordered by loop type",
    href: "/resources/loop-decision-guide-by-system.pdf",
    fileName: "Decision guide for a diabetes loop, ordered by loop type.pdf",
  },
  {
    title: "Decision guide for a diabetes loop, ordered by axis",
    href: "/resources/loop-decision-guide-by-question.pdf",
    fileName: "Decision guide for a diabetes loop, ordered by axis.pdf",
  },
];

const Page = () => {
  usePageView();

  return (
    <>
      <Seo
        title="Decision guide for a diabetes loop"
        description="Download the research guide comparing SmartGuard, myLoop, Control-IQ, and AAPS, ordered by loop type or by decision axis."
      />
      <Box sx={{ backgroundColor: "background.paper", minHeight: "100vh" }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            pt: { xs: 10, md: 14 },
            pb: { xs: 3, md: 4 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 760 }}>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "primary.main",
                  display: "block",
                  mb: 1.5,
                }}
              >
                Resources
              </Typography>
              <Typography
                component="h1"
                variant="h1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "-0.03em",
                  lineHeight: 1.05,
                  fontSize: { xs: "2.4rem", md: "3.5rem" },
                }}
              >
                Decision guide for a diabetes loop
              </Typography>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
          <Stack spacing={4} sx={{ maxWidth: 760 }}>
            <Box
              sx={{
                borderLeft: 3,
                borderColor: "primary.main",
                pl: 2.5,
                py: 0.5,
              }}
            >
              <Typography variant="body2" sx={bodySx}>
                Based on interviews with diabetologists (13), users (30), and a questionnaire (82), by the Institute of Sociology of the Czech Academy of Sciences, with physicians from the Third Faculty of Medicine, Charles University, and the patient organisation PaciMed. It describes systems available in the Czech Republic, including AAPS. The Medtrum patch pump is covered only together with AAPS. These are shared experiences, not a personal treatment recommendation.
              </Typography>
            </Box>

            <Stack spacing={2}>
              {pdfs.map((pdf) => (
                <Stack
                  key={pdf.href}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  sx={{
                    alignItems: { sm: "center" },
                    justifyContent: "space-between",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 2,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {pdf.title}
                  </Typography>
                  <Button
                    component="a"
                    href={pdf.href}
                    download={pdf.fileName}
                    variant="contained"
                    sx={{ flexShrink: 0 }}
                  >
                    Download PDF
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export default Page;
